import { useState, useEffect, useCallback } from 'react';
import ProjectService from '../services/projectService';
import Toast from '../components/ui/toast';

/**
 * Hook para gerenciar projetos com cache
 */
export const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache dos projetos por ID para acesso rápido
  const [projectsById, setProjectsById] = useState({});

  const loadProjects = useCallback(async (forceReload = false) => {
    // Se já temos projetos e não é reload forçado, não buscar novamente
    if (projects.length > 0 && !forceReload) {
      return projects;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await ProjectService.getAll({
        page: 1,
        pageSize: 100 // Buscar todos para cache
      });
      
      const projectsData = response.data?.response?.data || [];
      const hasLogoUrls = projectsData.some(p => p.logoUrl);
      const formattedProjects = projectsData.map(p => ({
        id: String(p.id),
        name: p.name,
        description: p.description || '',
        type: p.type || 'Projeto',
        status: p.active ? 'Ativo' : 'Inativo',
        active: p.active,
        logoUrl: hasLogoUrls ? p.logoUrl : null,
      }));
      
      setProjects(formattedProjects);
      
      // Criar cache por ID
      const cache = {};
      formattedProjects.forEach(project => {
        cache[project.id] = project;
      });
      setProjectsById(cache);
      
      return formattedProjects;
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [projects.length]);

  // Buscar projeto por ID
  const getProjectById = useCallback((id) => {
    return projectsById[id] || null;
  }, [projectsById]);

  // Buscar projetos por lista de IDs
  const getProjectsByIds = useCallback((ids) => {
    if (!Array.isArray(ids)) return [];
    return ids.map(id => projectsById[id]).filter(Boolean);
  }, [projectsById]);

  // Carregar projetos na primeira execução
  useEffect(() => {
    loadProjects();
  }, []);

  return {
    projects,
    loading,
    error,
    loadProjects,
    getProjectById,
    getProjectsByIds,
    // Métrica úteis
    totalProjects: projects.length,
    activeProjects: projects.filter(p => p.active).length
  };
};

/**
 * Hook personalizado para gerenciar operações de projetos
 * Centraliza a lógica de CRUD e manipulação de estado
 */
const useProjectsOld = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });

  // Carregar projetos com paginação
  const loadProjects = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        search: params.search || '',
        sorterField: params.sorterField || '',
        sortOrder: params.sortOrder || 'desc'
      };

      const response = await ProjectService.getAll(requestParams);
      const responseData = response?.data?.response || response?.data;
      const projectsRaw = responseData?.data || responseData || [];
      
      // Mapear dados do backend para frontend
      const mappedProjects = projectsRaw.map(project => ProjectService.mapToFrontend(project));
      
      setProjects(mappedProjects);
      
      // Atualizar paginação
      const totalRecords = responseData?.total || responseData?.totalRecords || projectsRaw.length;
      setPagination(prev => ({
        ...prev,
        current: requestParams.page,
        pageSize: requestParams.pageSize,
        total: totalRecords
      }));

      return {
        data: mappedProjects,
        total: totalRecords,
        success: true
      };
      
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      Toast.error('Erro ao carregar projetos');
      setProjects([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      
      return {
        data: [],
        total: 0,
        success: false,
        error
      };
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  // Carregar projeto por ID
  const getProject = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await ProjectService.getById(id);
      
      if (response?.data) {
        return {
          data: ProjectService.mapToFrontend(response.data),
          success: true
        };
      }
      
      return { data: null, success: false };
      
    } catch (error) {
      console.error('Erro ao carregar projeto:', error);
      Toast.error('Projeto não encontrado');
      return { data: null, success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Criar ou atualizar projeto
  const saveProject = useCallback(async (projectData) => {
    try {
      setLoading(true);
      
      // Mapear dados para o backend
      const backendData = ProjectService.mapToBackend(projectData);
      const response = await ProjectService.upsert(backendData);
      
      if (response?.data) {
        const savedProject = ProjectService.mapToFrontend(response.data);
        
        // Atualizar lista local
        setProjects(prev => {
          const existingIndex = prev.findIndex(p => p.id === savedProject.id);
          if (existingIndex >= 0) {
            // Atualizar existente
            const updated = [...prev];
            updated[existingIndex] = savedProject;
            return updated;
          } else {
            // Adicionar novo
            return [savedProject, ...prev];
          }
        });

        const message = projectData.isNew ? 'Projeto criado com sucesso!' : 'Projeto atualizado com sucesso!';
        Toast.success(message);
        
        return {
          data: savedProject,
          success: true
        };
      }
      
      return { data: null, success: false };
      
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      Toast.error('Erro ao salvar projeto');
      return { data: null, success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Deletar projeto
  const deleteProject = useCallback(async (projectId, projectName) => {
    try {
      setLoading(true);
      await ProjectService.delete(projectId);
      
      // Remover da lista local
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // Atualizar total da paginação
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
      
      Toast.success(`Projeto "${projectName}" excluído com sucesso!`);
      
      return { success: true };
      
    } catch (error) {
      console.error('Erro ao excluir projeto:', error);
      Toast.error('Erro ao excluir projeto');
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload de arquivos
  const uploadFile = useCallback(async (projectId, file, type = 'logo') => {
    try {
      setLoading(true);
      
      const uploadFunction = type === 'logo' ? ProjectService.uploadLogo : ProjectService.uploadOrcamento;
      const response = await uploadFunction(projectId, file);
      
      if (response?.data) {
        // Atualizar projeto na lista local
        setProjects(prev => prev.map(p => {
          if (p.id === projectId) {
            return {
              ...p,
              [type === 'logo' ? 'files' : 'fileOrcamento']: response.data,
              icon: type === 'logo' ? '🖼️' : p.icon
            };
          }
          return p;
        }));
        
        const message = type === 'logo' ? 'Logo enviado com sucesso!' : 'Planilha enviada com sucesso!';
        Toast.success(message);
        
        return {
          data: response.data,
          success: true
        };
      }
      
      return { data: null, success: false };
      
    } catch (error) {
      console.error(`Erro ao fazer upload de ${type}:`, error);
      Toast.error(`Erro ao enviar ${type}`);
      return { data: null, success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Remover arquivo
  const removeFile = useCallback(async (projectId, type = 'logo') => {
    try {
      setLoading(true);
      
      const removeFunction = type === 'logo' ? ProjectService.removeLogo : ProjectService.removeOrcamento;
      await removeFunction(projectId);
      
      // Atualizar projeto na lista local
      setProjects(prev => prev.map(p => {
        if (p.id === projectId) {
          return {
            ...p,
            [type === 'logo' ? 'files' : 'fileOrcamento']: null,
            icon: type === 'logo' ? '📁' : p.icon
          };
        }
        return p;
      }));
      
      const message = type === 'logo' ? 'Logo removido com sucesso!' : 'Planilha removida com sucesso!';
      Toast.success(message);
      
      return { success: true };
      
    } catch (error) {
      console.error(`Erro ao remover ${type}:`, error);
      Toast.error(`Erro ao remover ${type}`);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Resetar estado
  const resetProjects = useCallback(() => {
    setProjects([]);
    setPagination({
      current: 1,
      pageSize: 12,
      total: 0
    });
  }, []);

  // Calcular estatísticas
  const getStats = useCallback(() => {
    return {
      total: pagination.total,
      active: projects.filter(p => p.status === 'active').length,
      inactive: projects.filter(p => p.status === 'inactive').length,
      pending: projects.filter(p => p.tags?.includes('Configurando')).length
    };
  }, [projects, pagination.total]);

  return {
    // Estado
    projects,
    loading,
    pagination,
    
    // Ações
    loadProjects,
    getProject,
    saveProject,
    deleteProject,
    uploadFile,
    removeFile,
    resetProjects,
    
    // Utilitários
    getStats,
    
    // Controle de paginação
    setPagination
  };
};

export default useProjects; 